import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Box, Button, Text } from 'grommet';
import { renderTemplatePreview } from '../../../../../utils';
import Loader from '../../../../../components/loader';
import { addError } from '../../../../../toasts/toasts.actions';

// Mirrors NegativeButtonCopy.SystemDefault in Minion.Offers.
const SYSTEM_DEFAULT_DECLINE_COPY = 'No Thanks';

// Default placeholder values for non-stacked templates.
const DEFAULT_PLACEHOLDER_VALUES = {
  placeholder__highlightlabel: 'Exclusive Offer',
  placeholder__title: 'Sign up for another offer!',
  placeholder__description:
    "We know you love offers so much, so here's an offer for another offer. " +
    "<a href='http://example.com/another/offer'>http://example.com/another/offer</a>",
  placeholder__calltoaction: 'Sign up for another offer!',
  placeholder__declineoffer: SYSTEM_DEFAULT_DECLINE_COPY,
  placeholder__disclaimer: 'Standard Disclaimer',
  placeholder__modaldescription:
    'Extended Disclaimer content <a href="http://example.com/another/offer">Offer Disclaimer</a>',
  placeholder__footertext: 'Sample footer text',
  placeholder__footerlinktext: 'Sample Footer link text',
  placeholder__privacytext: 'Sample Privacy Text',
  placeholder__thumbnailurl:
    'https://assets.fluentpulse.com/dev/adcreative-thumbnail-1c2a6aea-1634-4f54-a327-ac5a6d224ff3-2023-12-19T19-07-40',
  placeholder__thumbnailalttext: 'Thumbnail Alt Text',
  placeholder__logoalttext: 'Logo Alt Text'
};

// Sample offer variations for stacked templates. Each offer has unique content to show variety in the preview.
const STACKED_OFFER_VARIATIONS = [
  {
    highlightlabel: 'Featured',
    title: 'Get 20% Off Your First Order',
    description:
      'Sign up today and receive an exclusive discount on your first purchase.',
    calltoaction: 'Claim Discount',
    thumbnailurl:
      'https://assets.fluentpulse.com/uat/adcreative-thumbnail-787ea44d-ba09-486a-ac07-cb0788da886e-2025-03-12T16-13-38'
  },
  {
    highlightlabel: 'Limited Time',
    title: 'Free Shipping on Orders $50+',
    description: 'Enjoy free standard shipping when you spend $50 or more.',
    calltoaction: 'Shop Now',
    thumbnailurl:
      'https://assets.fluentpulse.com/uat/adcreative-thumbnail-9507c7c3-b0d7-4c5f-ab03-27b9b75264a1-2024-11-22T15-18-40'
  },
  {
    highlightlabel: 'Popular',
    title: 'Join Our Rewards Program',
    description:
      'Earn points on every purchase and redeem for exclusive rewards.',
    calltoaction: 'Join Free',
    thumbnailurl:
      'https://assets.fluentpulse.com/uat/adcreative-thumbnail-e2fbaf10-9c84-4a98-9e24-a8b4b3fca4a6-2024-04-17T18-18-56'
  },
  {
    highlightlabel: 'New',
    title: 'Subscribe & Save 15%',
    description: 'Set up auto-delivery and save 15% on every order.',
    calltoaction: 'Subscribe',
    thumbnailurl:
      'https://assets.fluentpulse.com/uat/adcreative-thumbnail-dcc2bc32-5d41-41fd-943a-6529f4c4dc90-2024-06-11T17-36-02'
  },
  {
    highlightlabel: 'Hot Deal',
    title: 'Refer a Friend, Get $10',
    description: 'Share with friends and you both get $10 off your next order.',
    calltoaction: 'Refer Now',
    thumbnailurl:
      'https://assets.fluentpulse.com/uat/adcreative-thumbnail-6b25c9d4-f512-4cc9-b20e-4565b011ed30-2024-04-17T17-47-14'
  }
];

// DOM selectors for template elements
const SELECTORS = {
  PRIMARY_HEADLINE: '.partner-main-callout h1',
  SECONDARY_HEADLINE: '.partner-secondary-callout h2',
  DESKTOP_LOGO: '.part-desktop-display',
  MOBILE_LOGO: '.part-mobile-display',
  NEGATIVE_BUTTON: '.part-btn-negative'
};

/**
 * Decline button copy, mirroring NegativeButtonCopy.Resolve on the server.
 * Sample offers supply their own copy, so only the override brings source copy through.
 */
const resolveNegativeButtonCopy = item => {
  if (!item.negativeButtonOverrideCampaignCopy) {
    return DEFAULT_PLACEHOLDER_VALUES.placeholder__declineoffer;
  }

  return item.negativeButtonDefaultCopy?.trim() || SYSTEM_DEFAULT_DECLINE_COPY;
};

// Ensures a value is a string, converting if necessary.
const ensureString = value =>
  typeof value === 'string' ? value : value ? String(value) : '';

// Extracts the body tag content from HTML, or returns the full HTML if no body tag exists.
const getBodyContent = html => {
  const bodyMatch = html?.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  return bodyMatch ? bodyMatch[0] : html;
};

// Shuffles an array using Fisher-Yates algorithm.
const shuffleArray = array => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

/**
 * Replaces placeholder tokens (placeholder__*) in HTML with sample preview values.
 * For stacked templates, each offer block gets unique randomized content.
 */
const replacePlaceholders = html => {
  if (!html) return html;

  const isStacked = getBodyContent(html).includes('data-ft-af-exp-stacked');

  if (!isStacked) {
    // Non-stacked: simple replacement with default values
    return html.replace(/placeholder__\w+/gi, match => {
      const key = match.toLowerCase();
      return DEFAULT_PLACEHOLDER_VALUES[key] || match;
    });
  }

  // Stacked: replace placeholders within each offer block with unique content
  const shuffledOffers = shuffleArray(STACKED_OFFER_VARIATIONS);
  let offerIndex = 0;

  // Match each offer block and replace its placeholders with unique offer data
  const offerBlockRegex = /(<div[^>]*class\s*=\s*["'][^"']*af-exp_offer-block[^"']*["'][^>]*>)([\s\S]*?)(<\/div>\s*(?=<div[^>]*af-exp_offer-block|$))/gi;

  let result = html.replace(
    offerBlockRegex,
    (_match, openTag, content, closeTag) => {
      const offer = shuffledOffers[offerIndex % shuffledOffers.length];
      offerIndex++;

      // Replace offer-specific placeholders within this block
      const updatedContent = content
        .replace(/placeholder__highlightlabel/gi, offer.highlightlabel)
        .replace(/placeholder__title/gi, offer.title)
        .replace(/placeholder__description/gi, offer.description)
        .replace(/placeholder__calltoaction/gi, offer.calltoaction)
        .replace(/placeholder__thumbnailurl/gi, offer.thumbnailurl);

      return openTag + updatedContent + closeTag;
    }
  );

  // Replace any remaining placeholders outside offer blocks with defaults
  result = result.replace(/placeholder__\w+/gi, match => {
    const key = match.toLowerCase();
    return DEFAULT_PLACEHOLDER_VALUES[key] || match;
  });

  return result;
};

/**
 * For stacked offer templates, shows all offer blocks.
 * Removes display:none from all .af-exp_offer-block elements
 * so they're visible in the preview.
 */
const showStackedOfferBlocks = html => {
  if (!html || !getBodyContent(html).includes('data-ft-af-exp-stacked'))
    return html;

  const regex = /(<div[^>]*class\s*=\s*["'][^"']*af-exp_offer-block[^"']*["'][^>]*style\s*=\s*["'])([^"']*)(display\s*:\s*none\s*;?)([^"']*["'])/gi;

  return html.replace(
    regex,
    (_match, prefix, beforeDisplay, _displayNone, suffix) =>
      `${prefix}${beforeDisplay}${suffix}`
  );
};

/**
 * Injects an empty style element for dynamic CSS updates.
 * This element is used to apply customCss and manualCssOverride at runtime.
 */
const injectPreviewStyleElement = html => {
  if (!html) return html;

  // Already has the element
  if (
    html.includes('id="preview-replace"') ||
    html.includes("id='preview-replace'")
  ) {
    return html;
  }

  const styleElement = '<style id="preview-replace"></style>';

  // Insert before closing body or html tag, or append to end
  if (html.includes('</body>')) {
    return html.replace('</body>', `${styleElement}\n</body>`);
  }
  if (html.includes('</html>')) {
    return html.replace('</html>', `${styleElement}\n</html>`);
  }
  return html + styleElement;
};

/**
 * Processes raw template HTML for preview display.
 * Applies all necessary transformations in sequence.
 */
const processTemplateHtml = html => {
  if (!html) return html;

  let processed = html;
  processed = showStackedOfferBlocks(processed);
  processed = injectPreviewStyleElement(processed);
  processed = replacePlaceholders(processed);

  return processed;
};

// Gets or creates the preview-replace style element in the iframe document.
const getOrCreatePreviewStyle = doc => {
  let styleEl = doc.getElementById('preview-replace');

  if (!styleEl) {
    styleEl = doc.createElement('style');
    styleEl.id = 'preview-replace';
    (doc.head || doc.body)?.appendChild(styleEl);
  }

  return styleEl;
};

/**
 * Updates text content of an element, targeting inner span if present.
 * Only updates if the value has actually changed (prevents unnecessary reflows).
 */
const updateElementText = (element, value) => {
  if (!element) return;

  const targetElement = element.querySelector('span') || element;
  const newValue = value || '';

  if (targetElement.innerHTML !== newValue) {
    targetElement.innerHTML = newValue;
  }
};

// Updates an image element's src attribute if changed.
const updateImageSrc = (container, url, altText) => {
  if (!container) return;

  const img = container.querySelector('img');
  if (!img) return;

  const newSrc = url ? encodeURI(url) : '';
  if (img.src !== newSrc) {
    img.src = newSrc;
  }

  if (altText && img.alt !== altText) {
    img.alt = altText;
  }
};

/**
 * Updates the iframe's DOM elements with current item values.
 * Directly manipulates the iframe DOM for instant preview updates.
 */
const updateIframeContent = (frame, item, isMobile = false) => {
  if (!frame || !item) return;

  try {
    const doc = frame.contentDocument || frame.contentWindow?.document;
    if (!doc || doc.readyState !== 'complete') return;

    const select = selector => doc.querySelector(selector);

    // Update headlines (mobile values take priority in mobile mode)
    const primaryHeadline = select(SELECTORS.PRIMARY_HEADLINE);
    if (primaryHeadline) {
      const value = isMobile
        ? item.mobilePrimaryHeadline || item.primaryHeadline
        : item.primaryHeadline || item.mobilePrimaryHeadline;
      updateElementText(primaryHeadline, value);
    }

    const secondaryHeadline = select(SELECTORS.SECONDARY_HEADLINE);
    if (secondaryHeadline) {
      const value = isMobile
        ? item.mobileSecondaryHeadline || item.secondaryHeadline
        : item.secondaryHeadline || item.mobileSecondaryHeadline;
      updateElementText(secondaryHeadline, value);
    }

    // Update decline buttons (stacked templates render several offer blocks)
    const negativeButtonCopy = resolveNegativeButtonCopy(item);
    doc
      .querySelectorAll(SELECTORS.NEGATIVE_BUTTON)
      .forEach(negativeButton =>
        updateElementText(negativeButton, negativeButtonCopy)
      );

    // Update logos
    updateImageSrc(
      select(SELECTORS.DESKTOP_LOGO),
      item.logoUrl,
      item.logoAltText
    );
    updateImageSrc(
      select(SELECTORS.MOBILE_LOGO),
      item.mobileLogoUrl,
      item.logoAltText
    );

    // Update CSS (theme CSS + manual overrides + stacked template fix)
    const styleEl = getOrCreatePreviewStyle(doc);
    const stackedCss = doc.querySelector('[data-ft-af-exp-stacked]')
      ? '[data-ft-af-exp-stacked] { height: auto !important; }'
      : '';
    const newCss =
      stackedCss +
      ensureString(item.customCss) +
      (item.manualCssOverride || '');

    if (styleEl.innerHTML !== newCss) {
      styleEl.innerHTML = newCss;
    }
  } catch (error) {
    // Cross-origin restrictions may prevent DOM access (shouldn't happen with blob URLs)
    addError?.('Unable to update preview due to cross-origin restrictions.');
  }
};

/**
 * AdunitPreview - Live preview component for ad unit templates.
 * Renders a template in an iframe and provides real-time updates as the user
 * modifies headlines, logos, and CSS styling. Uses direct DOM manipulation
 * for instant updates without iframe reloads.
 */
const AdunitPreview = ({ item, previewMode, addError }) => {
  const isLegacyTemplate =
    item.type === 'embeddedflowconfig' && !item?.adUnitTemplateId;
  const templateId = item?.adUnitTemplateId;
  const isMobile = previewMode === 'mobile';

  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  // Refs to persist values across renders without triggering re-renders
  const urlRef = useRef(null);
  const htmlRef = useRef(null);

  // Handles iframe load event - applies initial content updates
  const handleIframeLoad = useCallback(() => {
    const frame = document.getElementById('previewFrame');
    if (frame && item) {
      updateIframeContent(frame, item, isMobile);
    }
  }, [item, isMobile]);

  /**
   * Effect: Load template HTML when templateId changes.
   * Fetches template from API, processes it, and creates a blob URL for the iframe.
   * Uses a cancelled flag to ignore stale responses when templateId changes rapidly.
   */
  useEffect(() => {
    let cancelled = false;

    const loadTemplate = async () => {
      // Cleanup previous blob URL
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }

      if (!templateId) {
        setPreviewUrl(null);
        setLoading(false);
        htmlRef.current = null;
        return;
      }

      setLoading(true);

      try {
        const html = await renderTemplatePreview(templateId);

        // Ignore stale response if templateId changed during fetch
        if (cancelled) return;

        if (html) {
          const processedHtml = processTemplateHtml(html);
          htmlRef.current = processedHtml;

          // Create blob URL for same-origin iframe access
          const blob = new Blob([processedHtml], { type: 'text/html' });
          const url = URL.createObjectURL(blob);
          urlRef.current = url;
          setPreviewUrl(url);
        } else {
          setPreviewUrl(null);
          htmlRef.current = null;
        }
      } catch (error) {
        // Ignore errors from cancelled requests
        if (cancelled) return;
        addError?.('Error loading template preview. Please try again.');
        setPreviewUrl(null);
        htmlRef.current = null;
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadTemplate();

    // Cleanup: cancel stale request and revoke blob URL
    return () => {
      cancelled = true;
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [templateId, addError]);

  /**
   * Effect: Update iframe content when item properties or preview mode changes.
   * Uses direct DOM manipulation for instant updates without iframe reload.
   * Note: We intentionally list specific item properties rather than the entire
   * item object to prevent unnecessary updates when unrelated properties change.
   */
  useEffect(() => {
    if (!htmlRef.current || !item || !templateId) return;

    const frame = document.getElementById('previewFrame');
    if (frame) {
      updateIframeContent(frame, item, isMobile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    templateId,
    isMobile,
    item?.primaryHeadline,
    item?.secondaryHeadline,
    item?.mobilePrimaryHeadline,
    item?.mobileSecondaryHeadline,
    item?.logoUrl,
    item?.mobileLogoUrl,
    item?.logoAltText,
    item?.negativeButtonDefaultCopy,
    item?.negativeButtonOverrideCampaignCopy,
    item?.customCss,
    item?.manualCssOverride
  ]);

  // Render loading/error state overlay
  const showOverlay = loading || !previewUrl || isLegacyTemplate;

  return (
    <Box
      border='border400'
      pad='small'
      round='small'
      fill='horizontal'
      margin={{ right: 'small' }}
      style={{ position: 'relative' }}
    >
      {showOverlay && (
        <Box
          align='center'
          justify='center'
          fill
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            backgroundColor: 'white'
          }}
        >
          {loading ? (
            <Loader />
          ) : isLegacyTemplate ? (
            <Text size='medium' color='text-weak'>
              Preview is not available for legacy templates. Preview is only
              available for templates with an Ad Unit Template ID.
            </Text>
          ) : (
            <Box align='center' justify='center' gap='medium'>
              <Text size='medium' weight='bold'>
                No preview available. Please check the template ID and try
                again.
              </Text>
              <Button label='Reload' onClick={() => window.location.reload()} />
            </Box>
          )}
        </Box>
      )}

      <iframe
        id='previewFrame'
        key={previewUrl || 'empty'}
        src={previewUrl || 'about:blank'}
        title='Ad Unit Preview'
        height='100%'
        style={{
          border: 'none',
          opacity: showOverlay ? 0 : 1,
          width: '100%'
        }}
        onLoad={handleIframeLoad}
      />
    </Box>
  );
};

export default AdunitPreview;
